import React, { useState } from "react";
import { Box } from "@mui/material";
import FolderDrawer from "./FolderDrawer";
import BreadcrumbNavigation from "./BreadcrumbNavigationUser";
import AllFilesView from "./AllFilesView";
import RootFolder from "./RootFolder";
import FolderView from "./FolderView";
import {
  useUserFolders,
  useUserSubfolders,
  useUserFiles,
  useAllUserFiles,
} from "../hooks/useUserFileHierarchy";
import FileManagementAppBar from "../../component/FileManagementAppBar";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "../../../../context/AuthContext";
import AddNewFolder from "./addNewFolder";
import { handleCreateFolder } from "../../../../shared/services/folderUtils";

function UserFileManager() {
  const { currentUser } = useAuth();

  const userId = currentUser?.uid;

  // State for selected folder and subfolder
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedSubfolder, setSelectedSubfolder] = useState(null);

  // Fetch top-level folders
  const {
    data: folders = [],
    isLoading: foldersLoading,
    error: foldersError,
    refetch: refetchFolders,
  } = useUserFolders();

  // Fetch subfolders for selected folder
  const {
    data: subfolders = [],
    isLoading: subfoldersLoading,
    error: subfoldersError,
    refetch: refetchSubfolders,
  } = useUserSubfolders(selectedFolder?.folderId);

  console.log("Subfolders data:", subfolders);
  console.log("Subfolders loading:", subfoldersLoading);
  console.log("Subfolders error:", subfoldersError);

  // Fetch files for selected folder and subfolder
  const {
    data: files = [],
    isLoading: filesLoading,
    error: filesError,
    refetch: refetchFiles,
  } = useUserFiles(selectedFolder?.folderId, selectedSubfolder?.id);

  console.log("Files data:", files);
  console.log("Files loading:", filesLoading);
  console.log("Files error:", filesError);

  // Fetch all files for "all files" view
  const {
    data: allFiles = [],
    isLoading: allFilesLoading,
    error: allFilesError,
    refetch: refetchAllFiles,
  } = useAllUserFiles();

  // Build breadcrumb items based on selection
  const breadcrumbItems = [
    {
      key: "root",
      label: <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
      show: true,
    },
  ];
  if (selectedFolder) {
    breadcrumbItems.push({
      key: selectedFolder.folderId,
      label: selectedFolder.folderName,
      show: true,
    });
  }
  if (selectedSubfolder) {
    breadcrumbItems.push({
      key: selectedSubfolder.id,
      label: selectedSubfolder.subfolderName,
      show: true,
    });
  }

  const activeIndex = breadcrumbItems.length - 1;

  const handleFolderSelect = (folder) => {
    console.log("Folder selected:", folder);
    setSelectedFolder(folder);
    setSelectedSubfolder(null);
  };

  const handleSubfolderSelect = (subfolder) => {
    console.log("Subfolder selected:", subfolder);
    setSelectedSubfolder(subfolder);
  };

  const handleBreadcrumbClick = (index) => {
    if (index === activeIndex) return;
    if (index === 0) {
      // Clicked on root breadcrumb
      setSelectedFolder(null);
      setSelectedSubfolder(null);
      return;
    }
    if (index === 1) {
      // Clicked on folder breadcrumb
      setSelectedSubfolder(null);
    }
  };

  // Callback to refresh data after upload/delete
  const refreshData = async () => {
    if (!selectedFolder) {
      await refetchAllFiles();
    } else if (selectedFolder && !selectedSubfolder) {
      await refetchSubfolders();
      await refetchFolders();
    } else if (selectedFolder && selectedSubfolder) {
      await refetchFiles();
      await refetchSubfolders();
      await refetchFolders();
    }
  };

  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
  const [addFolderLoading, setAddFolderLoading] = useState(false);
  const [addFolderError, setAddFolderError] = useState(null);

  const handleAddFolderClick = () => {
    setAddFolderError(null);
    setAddFolderDialogOpen(true);
  };

  const handleAddFolderClose = () => {
    setAddFolderDialogOpen(false);
    setAddFolderError(null);
  };

  const handleAddFolderSubmit = async (folderName) => {
    setAddFolderLoading(true);
    setAddFolderError(null);
    try {
      await handleCreateFolder({
        userId,
        folderName,
        onSuccess: () => {
          setAddFolderDialogOpen(false);
          refreshData();
        },
        onError: (error) => {
          setAddFolderError(
            error.message || "Failed to create folder. Please try again."
          );
        },
      });
      refetchFolders();
    } catch (error) {
      setAddFolderError(
        error.message || "Failed to create folder. Please try again."
      );
    } finally {
      setAddFolderLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <FileManagementAppBar />
      <FolderDrawer
        folders={folders}
        foldersLoading={foldersLoading}
        foldersError={foldersError}
        selectedFolder={selectedFolder}
        onFolderSelect={handleFolderSelect}
        onAddFolder={handleAddFolderClick}
        refetchFolders={refetchFolders}
      />
      <Box sx={{ flexGrow: 1, p: 2, overflow: "auto" }}>
        <BreadcrumbNavigation
          breadcrumbItems={breadcrumbItems}
          activeIndex={activeIndex}
          onBreadcrumbClick={handleBreadcrumbClick}
        />
        {!selectedFolder && (
          <AllFilesView
            files={allFiles}
            loading={allFilesLoading}
            error={allFilesError}
          />
        )}
        {selectedFolder && !selectedSubfolder && (
          <RootFolder
            folder={selectedFolder}
            subfolders={subfolders}
            subfoldersLoading={subfoldersLoading}
            subfoldersError={subfoldersError}
            onSubfolderSelect={handleSubfolderSelect}
            selectedSubfolderId={
              selectedSubfolder ? selectedSubfolder.id : null
            }
          />
        )}
        {selectedSubfolder && (
          <FolderView
            folder={selectedFolder}
            subfolder={selectedSubfolder}
            files={files}
            filesLoading={filesLoading}
            filesError={filesError}
            onFolderUpdate={refreshData}
            userId={userId}
            folderId={selectedFolder?.folderId}
          />
        )}
      </Box>
      <AddNewFolder
        open={addFolderDialogOpen}
        onClose={handleAddFolderClose}
        onSubmit={handleAddFolderSubmit}
        loading={addFolderLoading}
        error={addFolderError}
      />
    </Box>
  );
}
export default UserFileManager;
